import { useEffect, useState } from 'react';
import api from '../api';
import { useLab } from '../i18n/LabContext';
import { useLang } from '../i18n/LanguageContext';

export default function Promotions() {
  const { t } = useLang();
  const { labId, currentLab } = useLab();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState(null); // null | {id?, title, description, discount_percent, expires_at, color_hex, image_url, is_active}

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get('/promotions/owner' + (labId ? `?lab_id=${labId}` : ''));
      setItems(r.data);
    } catch (e) { console.error(e); }
    setLoading(false);
  };

  useEffect(() => { load(); }, [labId]); // eslint-disable-line

  const openNew = () => setForm({
    lab_id: labId,
    title: '', description: '', discount_percent: 10,
    expires_at: '', color_hex: '#FF1E6E', image_url: '', is_active: true,
  });
  const openEdit = (p) => setForm({ ...p });

  const save = async () => {
    if (!form.title.trim()) return alert(t('alertTitleRequired'));
    try {
      if (form.id) {
        await api.patch(`/promotions/${form.id}`, form);
      } else {
        await api.post('/promotions', { ...form, lab_id: labId });
      }
      setForm(null);
      load();
    } catch (e) { alert(e.response?.data?.error || 'Xato'); }
  };

  const del = async (id) => {
    if (!window.confirm(t('confirmDeletePromo'))) return;
    try { await api.delete(`/promotions/${id}`); load(); }
    catch (e) { alert(e.response?.data?.error || 'Xato'); }
  };

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
        <div>
          <h2 style={{ margin: 0, color: 'var(--text)' }}>{t('promotionsTitle')}</h2>
          <div style={{ color: 'var(--text3)', fontSize: 13, marginTop: 4 }}>
            {currentLab?.name || t('clubNotSelected')} — {t('promotionsSubtitle')}
          </div>
        </div>
        <button onClick={openNew} style={{
          background: 'var(--mag)', color: '#fff', border: 'none', padding: '10px 20px',
          borderRadius: 10, cursor: 'pointer', fontSize: 13, fontWeight: 700,
        }}>{t('addPromotion')}</button>
      </div>

      {loading ? (
        <div style={{ color: 'var(--text3)', padding: 40, textAlign: 'center' }}>{t('loading')}</div>
      ) : items.length === 0 ? (
        <div style={{ background: 'var(--card2)', border: '1px dashed var(--border)', borderRadius: 12, padding: 40, textAlign: 'center' }}>
          <div style={{ fontSize: 48, marginBottom: 12 }}>🎁</div>
          <div style={{ color: 'var(--text2)', fontSize: 15, marginBottom: 8 }}>{t('noPromotions')}</div>
          <div style={{ color: 'var(--text3)', fontSize: 12 }}>{t('noPromotionsHint')}</div>
        </div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', gap: 14 }}>
          {items.map(p => (
            <div key={p.id} style={{
              background: `linear-gradient(135deg, ${p.color_hex || '#FF1E6E'}18, var(--card2))`,
              border: `1.5px solid ${p.color_hex || '#FF1E6E'}${p.is_active ? '' : '50'}`,
              borderRadius: 14, padding: 16, opacity: p.is_active ? 1 : 0.55,
              position: 'relative', overflow: 'hidden',
            }}>
              {p.image_url && (
                <img src={p.image_url.startsWith('http') ? p.image_url : `${(process.env.REACT_APP_API_URL || 'http://localhost:3000/api').replace('/api', '')}${p.image_url}`}
                  alt="" style={{ width: '100%', height: 120, objectFit: 'cover', borderRadius: 8, marginBottom: 10 }} />
              )}
              <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 8 }}>
                {p.discount_percent > 0 && (
                  <span style={{
                    background: p.color_hex || '#FF1E6E', color: '#000', padding: '3px 10px',
                    borderRadius: 6, fontWeight: 900, fontSize: 13,
                  }}>−{p.discount_percent}%</span>
                )}
                <span style={{
                  background: p.is_active ? 'rgba(0,255,136,0.15)' : 'rgba(107,114,128,0.15)',
                  color: p.is_active ? '#00ff88' : '#6b7280',
                  padding: '2px 8px', borderRadius: 6, fontSize: 10, fontWeight: 700,
                }}>{p.is_active ? `● ${t('statusActivePromo')}` : `○ ${t('statusInactive')}`}</span>
              </div>
              <div style={{ fontSize: 16, fontWeight: 900, color: 'var(--text)', marginBottom: 4 }}>{p.title}</div>
              {p.description && (
                <div style={{ fontSize: 12, color: 'var(--text2)', marginBottom: 10 }}>{p.description}</div>
              )}
              <div style={{ fontSize: 11, color: 'var(--text3)' }}>
                {p.expires_at ? `${t('expiresOn')}: ${new Date(p.expires_at).toLocaleDateString('uz-UZ')}` : t('unlimited')}
              </div>
              <div style={{ display: 'flex', gap: 6, marginTop: 12 }}>
                <button onClick={() => openEdit(p)} style={{
                  flex: 1, background: 'rgba(0,212,255,0.1)', border: '1px solid var(--cyan)',
                  color: 'var(--cyan)', padding: '6px', borderRadius: 8, cursor: 'pointer', fontSize: 11, fontWeight: 700,
                }}>✎ {t('editItem')}</button>
                <button onClick={() => del(p.id)} style={{
                  background: 'rgba(255,59,92,0.1)', border: '1px solid var(--red)',
                  color: 'var(--red)', padding: '6px 10px', borderRadius: 8, cursor: 'pointer', fontSize: 11,
                }}>✕</button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Modal — form */}
      {form && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.8)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 999 }}>
          <div style={{ background: 'var(--card2)', borderRadius: 16, padding: 24, width: 480, maxWidth: '90vw', border: '1px solid var(--border)' }}>
            <div style={{ fontSize: 18, fontWeight: 900, marginBottom: 16, color: 'var(--text)' }}>
              {form.id ? `✎ ${t('editPromotion')}` : `+ ${t('newPromotion')}`}
            </div>
            <div style={{ display: 'grid', gap: 10 }}>
              <input value={form.title} onChange={e => setForm({...form, title: e.target.value})}
                placeholder={`${t('titleLabel')} *`} maxLength={120}
                style={inp} />
              <textarea value={form.description} onChange={e => setForm({...form, description: e.target.value})}
                placeholder={t('descLabel')} maxLength={500} rows={3}
                style={{...inp, resize: 'vertical'}} />
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                <input type="number" min="0" max="100" value={form.discount_percent}
                  onChange={e => setForm({...form, discount_percent: parseInt(e.target.value) || 0})}
                  placeholder={t('discountPct')} style={inp} />
                <input type="datetime-local" value={form.expires_at?.slice(0, 16) || ''}
                  onChange={e => setForm({...form, expires_at: e.target.value})}
                  placeholder={t('expiresAtLabel')} style={inp} />
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '80px 1fr', gap: 10, alignItems: 'center' }}>
                <input type="color" value={form.color_hex || '#FF1E6E'}
                  onChange={e => setForm({...form, color_hex: e.target.value})}
                  style={{ width: '100%', height: 40, border: 'none', borderRadius: 8, background: 'transparent' }} />
                <input value={form.image_url} onChange={e => setForm({...form, image_url: e.target.value})}
                  placeholder={t('imageUrlLabel')} style={inp} />
              </div>
              <label style={{ display: 'flex', alignItems: 'center', gap: 8, color: 'var(--text2)', fontSize: 13, cursor: 'pointer' }}>
                <input type="checkbox" checked={form.is_active}
                  onChange={e => setForm({...form, is_active: e.target.checked})} />
                {t('activeLabel')}
              </label>
            </div>
            <div style={{ display: 'flex', gap: 10, marginTop: 20 }}>
              <button onClick={() => setForm(null)} style={{
                flex: 1, background: 'transparent', border: '1px solid var(--border)', color: 'var(--text3)',
                padding: '10px', borderRadius: 10, cursor: 'pointer', fontWeight: 700,
              }}>{t('cancel').toUpperCase()}</button>
              <button onClick={save} style={{
                flex: 2, background: 'var(--mag)', border: 'none', color: '#fff',
                padding: '10px', borderRadius: 10, cursor: 'pointer', fontWeight: 700,
              }}>{t('save').toUpperCase()}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const inp = {
  background: 'var(--bg2)', border: '1px solid var(--border)', color: 'var(--text)',
  padding: '10px 12px', borderRadius: 8, fontSize: 13,
};
